import {
  sanitizePhoneInput,
  validateCompanyNameText,
  validateEmailAddress,
  validateLettersSpacesCommasNameRequired,
  type ToolbarSearchableSelectOption,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CustomerEditDrawerInitialValues } from "../../../shared/store/customers/customers.models";
import type { CustomerFormDrawerMode, CustomerFormSubmitPayload } from "../types";

const INVITE_FIELD_KEYS = [
  "companyName",
  "primaryContactName",
  "primaryContactEmail",
  "locations",
] as const;

const EDIT_FIELD_KEYS = ["companyName", "primaryContactName", "locations"] as const;

type FieldKey = (typeof INVITE_FIELD_KEYS)[number];

const PRIMARY_BUSINESS_ADDRESS_MAX_LENGTH = 200;

export interface UseCustomerFormDrawerParams {
  mode: CustomerFormDrawerMode;
  open: boolean;
  locationOptions: ToolbarSearchableSelectOption[];
  initialValues?: CustomerEditDrawerInitialValues | null;
  customerId?: number;
}

export function useCustomerFormDrawer(params: UseCustomerFormDrawerParams) {
  const { mode, open, locationOptions, initialValues = null, customerId } = params;
  const isEditMode = mode === "edit";

  const [companyName, setCompanyName] = useState("");
  const [primaryContactName, setPrimaryContactName] = useState("");
  const [primaryContactEmail, setPrimaryContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [primaryBusinessAddress, setPrimaryBusinessAddress] = useState("");
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validationFieldKeys = useMemo(
    (): readonly FieldKey[] => (isEditMode ? EDIT_FIELD_KEYS : INVITE_FIELD_KEYS),
    [isEditMode],
  );

  const resetInviteForm = useCallback(() => {
    setCompanyName("");
    setPrimaryContactName("");
    setPrimaryContactEmail("");
    setPhone("");
    setPrimaryBusinessAddress("");
    setSelectedLocationIds([]);
    setFieldErrors({});
  }, []);

  const applyInitialValues = useCallback((values: CustomerEditDrawerInitialValues) => {
    setCompanyName(values.companyName);
    setPrimaryContactName(values.primaryContactName);
    setPrimaryContactEmail(values.primaryContactEmail);
    setPhone(sanitizePhoneInput(values.phone ?? ""));
    setPrimaryBusinessAddress(values.primaryBusinessAddress);
    setSelectedLocationIds(values.locationIds.map(String));
    setFieldErrors({});
  }, []);

  useEffect(() => {
    if (!open) return;
    if (isEditMode) {
      if (initialValues) {
        applyInitialValues(initialValues);
      }
      return;
    }
    resetInviteForm();
  }, [open, isEditMode, initialValues, applyInitialValues, resetInviteForm]);

  const fieldErrorFor = useCallback(
    (field: FieldKey): string | undefined => {
      switch (field) {
        case "companyName":
          return validateCompanyNameText(companyName);
        case "primaryContactName":
          return validateLettersSpacesCommasNameRequired(primaryContactName);
        case "primaryContactEmail":
          if (isEditMode) {
            return primaryContactEmail.trim()
              ? validateEmailAddress(primaryContactEmail, { required: false, minLen: 3, maxLen: 50 })
              : undefined;
          }
          return validateEmailAddress(primaryContactEmail, { required: true, minLen: 3, maxLen: 50 });
        case "locations":
          return selectedLocationIds.length > 0 ? undefined : "Select at least one location";
        default:
          return undefined;
      }
    },
    [companyName, isEditMode, primaryContactEmail, primaryContactName, selectedLocationIds],
  );

  const setSingleFieldError = useCallback((field: FieldKey, message: string | undefined) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (message) next[field] = message;
      else delete next[field];
      return next;
    });
  }, []);

  const validateFieldOnBlur = useCallback(
    (field: FieldKey) => {
      setSingleFieldError(field, fieldErrorFor(field));
    },
    [fieldErrorFor, setSingleFieldError],
  );

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    for (const key of validationFieldKeys) {
      const msg = fieldErrorFor(key);
      if (msg) errs[key] = msg;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [fieldErrorFor, validationFieldKeys]);

  const isFormValid = validationFieldKeys.every((key) => fieldErrorFor(key) === undefined);

  const buildPayload = useCallback((): CustomerFormSubmitPayload => ({
    customerId,
    companyName: companyName.trim(),
    primaryContactName: primaryContactName.trim(),
    primaryContactEmail: primaryContactEmail.trim(),
    phone: phone.trim(),
    primaryBusinessAddress: primaryBusinessAddress.trim(),
    locationIds: selectedLocationIds
      .map((id) => parseInt(id, 10))
      .filter((id) => Number.isFinite(id)),
  }), [
    companyName,
    customerId,
    phone,
    primaryBusinessAddress,
    primaryContactEmail,
    primaryContactName,
    selectedLocationIds,
  ]);

  const mergedLocationOptions = useMemo(() => {
    if (!isEditMode || selectedLocationIds.length === 0) {
      return locationOptions;
    }
    const optionIds = new Set(locationOptions.map((o) => o.value));
    const extras: ToolbarSearchableSelectOption[] = [];
    for (const id of selectedLocationIds) {
      if (!optionIds.has(id)) {
        extras.push({ value: id, label: `Location ${id}` });
      }
    }
    return extras.length > 0 ? [...locationOptions, ...extras] : locationOptions;
  }, [isEditMode, locationOptions, selectedLocationIds]);

  return {
    isEditMode,
    companyName,
    setCompanyName,
    primaryContactName,
    setPrimaryContactName,
    primaryContactEmail,
    setPrimaryContactEmail,
    phone,
    setPhone,
    primaryBusinessAddress,
    setPrimaryBusinessAddress,
    selectedLocationIds,
    setSelectedLocationIds,
    fieldErrors,
    validationFieldKeys,
    fieldErrorFor,
    setSingleFieldError,
    validateFieldOnBlur,
    validate,
    isFormValid,
    buildPayload,
    mergedLocationOptions,
    primaryBusinessAddressMaxLength: PRIMARY_BUSINESS_ADDRESS_MAX_LENGTH,
  };
}
